import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Vat } from 'depoto-core/src/entities'
import { NotificationService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { historyBack } from '../../utils'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { VatService } from '../../services/entity-services/vat.services'
import { updateCachedListQueries } from '../../services/query-cache.service'

export const vatSchema = {
  id: null,
  name: null,
  percent: null,
  default: null,
  coefficient: null,
  externalId: null,
  company: {
    id: null,
    name: null,
  },
  created: null,
  updated: null,
}

@Component({
  selector: 'app-vat',
  templateUrl: 'vat.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class VatComponent implements CheckDataChanges, OnDestroy {
  private vatService = inject(VatService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  private initialFormValue = signal<any>(null)
  submitted = signal(false)

  vatForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
    percent: new FormControl({ value: 0, disabled: false }, [Validators.required]),
    default: new FormControl({ value: false, disabled: false }, []),
  })

  readonly queryVat = injectQuery(() => ({
    queryKey: ['vat', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.vatService.getVat(id, vatSchema)
      } else {
        return new Vat()
      }
    },
    onSuccess: data => {
      this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.vat-id')
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      this.fillFormValue()
    },
  }))

  createVat = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.vatForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()

      return await this.vatService.createVat(n, vatSchema) // todo add schema
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.vatForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.vatForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.vat.created'))
      // Update the detail cache for this VAT so consumers see fresh data immediately
      this.queryClient.setQueryData(['vat', Number(data.id)], data)
      // OPTIONS: update options/list caches so selects/filters see the new vat immediately
      updateCachedListQueries(this.queryClient, ['vats', 'options'], data, { mode: 'insert', insertPosition: 'start' })

      // LISTING ['vats', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeVatListings()

      this.router.navigate(['/vat/', data['id']], { replaceUrl: true })
    },
  }))

  updateVat = injectMutation(() => ({
    mutationFn: () => {
      if (this.vatForm.invalid) {
        throw new Error('Form is invalid')
      }
      const vatDataToSave = this.getDataToSave()
      return this.vatService.updateVat(vatDataToSave, vatSchema)
    },
    onSuccess: async data => {
      console.log('updateVat wtf')
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.vatForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.vatForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.vat.updated'))
      // Update detail cache for this VAT
      this.queryClient.setQueryData(['vat', Number(data.id)], data)
      // OPTIONS: update options cache value for this vat
      updateCachedListQueries(this.queryClient, ['vats', 'options'], data, { mode: 'update' })

      // LISTING ['vats', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeVatListings()

      this.router.navigate(['/vat', data['id']])
    },
  }))

  deleteVat = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      const confirmation = confirm(
        this.translateService.instant('vat.delete-confirm', { name: this.vatForm.value.name })
      )
      if (confirmation) {
        await this.vatService.deleteVat(this.queryVat.data()).then(() => {
          this.notificationService.success(this.translateService.instant('alert.vat.deleted'))

          // const idStr = String(this.router.url.split('/')[2])
          const idStr = String(this.route.snapshot.params['id'])
          // OPTIONS: update options cache by removing the vat
          updateCachedListQueries(this.queryClient, ['vats', 'options'], { id: Number(idStr) } as any, {
            mode: 'delete',
          })

          // LISTING ['vats', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
          this.removeFromListingsIfPresent(Number(idStr))

          this.router.navigate(['/vats'], { replaceUrl: true })
        })
      }
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return this.queryVat.isFetching() || this.queryVat.isPending()
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    effect(() => {
      const data = this.queryVat.data()
      if (data) {
        const formValue = {
          name: data.name,
          percent: data.percent,
          default: data.default,
        }
        this.vatForm.patchValue(formValue, { emitEvent: false })
        this.initialFormValue.set(structuredClone(formValue))
        this.vatForm.markAsPristine()
      }
    })
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different vat ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  public fillFormValue() {
    const data = this.queryVat.data() as any
    const value = {
      name: data?.name ?? '',
      percent: data?.percent ?? 0,
      default: data?.default ?? false,
    }
    this.vatForm.setValue(value)
    // Snapshot initial values and mark pristine for unsaved-changes detection
    this.initialFormValue.set(structuredClone(value))
    this.vatForm.markAsPristine()
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.vatForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // Fallback: if initial snapshot not ready yet
    if (!initial) {
      return this.vatForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))

    return (
      norm(current?.name) !== norm(initial?.name) ||
      String(current?.percent ?? '') !== String(initial?.percent ?? '') ||
      String(current?.default ?? false) !== String(initial?.default ?? false)
    )
  }

  getDataToSave() {
    const current = this.vatForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Required fields
    patch.name = current.name
    patch.percent = current.percent

    if (this.isCreating()) patch.default = current.default

    // Optional fields — include only when changed
    if (!this.isCreating()) {
      const changedDefault = (current?.default ?? false) !== (initial?.default ?? false)
      if (changedDefault) patch.default = current.default
    }

    return patch
  }

  goBack(): void {
    historyBack()
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.vatForm.controls
  }

  // ===== Helpers for cache handling of vat listings =====
  private isVatsListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'vats' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  // CREATE → refetch all cached vat listings (['vats', sortProp, sortDir])
  private removeVatListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['vats'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['vats'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isVatsListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }
}
